import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { FaPaperPlane, FaSpinner, FaTimes, FaAd, FaRedo, FaCheckCircle, FaCalendarAlt, FaEye, FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import AdsSubNav from '../../Common/AdsSubNav';

const RunningFreeAds = () => {
  const navigate = useNavigate();
  const [runningFreeAds, setRunningFreeAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const totalPages = Math.ceil(runningFreeAds.length / rowsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const paginatedAds = [...runningFreeAds].reverse()
    .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const stats = {
    runningAds: runningFreeAds.length,
  };

  const fetchFreeAds = async (showRefresh = false) => {
    if (showRefresh) setIsRefreshing(true);
    if (!showRefresh) setLoading(true);
    try {
      const runningResponse = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/get/running/free/ads`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const runningFreeAdsData = runningResponse.data.data || [];
      setRunningFreeAds(runningFreeAdsData);
      setError(null);
    } catch (err) {
      setError("Failed to load data.");
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchFreeAds();
  }, []);

  const getMediaUrl = (screenshot) => {
    if (!screenshot) return null;
    return screenshot.startsWith('http')
      ? screenshot
      : `${process.env.REACT_APP_API_BASE_URL}/${screenshot.replace(/\\/g, "/")}`;
  };

  return (
    <div className="min-h-screen bg-slate-50/50 pb-12">
      {/* Modern Header - Compact for Mobile */}
      <div className="max-w-7xl mx-auto mb-3 sm:mb-6 px-2 sm:px-4 mt-2 sm:mt-6">
        <div className="bg-gradient-to-r from-amber-500 via-orange-600 to-amber-700 rounded-xl sm:rounded-2xl shadow-xl p-2 sm:p-6 text-white relative overflow-hidden">
          {/* Decorative background element */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none" />

          <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
              <button
                onClick={() => navigate(-1)}
                className="px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 text-white text-[9px] font-black uppercase tracking-tight border border-white/20 transition-all whitespace-nowrap shadow-sm"
              >
                ← Back
              </button>
              <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                <div className="p-2 sm:p-3 bg-white/20 rounded-lg sm:rounded-xl backdrop-blur-md border border-white/30 hidden xs:flex">
                  <FaAd className="text-sm sm:text-2xl text-white" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-sm sm:text-2xl md:text-3xl font-black tracking-tight truncate">Running Reward Ads</h1>
                  <p className="text-orange-100/80 text-[9px] sm:text-sm font-bold uppercase tracking-wider mt-0.5 truncate">Currently active & under review</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <div className="bg-white/20 backdrop-blur-md rounded-lg sm:rounded-xl p-2 sm:px-4 sm:py-3 border border-white/30 text-center flex-1 sm:flex-none min-w-[70px] sm:min-w-[100px] shadow-lg">
                <div className="text-sm sm:text-2xl font-black">{stats.runningAds || 0}</div>
                <div className="text-[8px] sm:text-xs font-black uppercase tracking-widest text-orange-100">Active</div>
              </div>
              <button
                onClick={() => fetchFreeAds(true)}
                disabled={loading || isRefreshing}
                className="p-2.5 sm:p-4 bg-white/20 hover:bg-white/30 rounded-lg sm:rounded-xl transition-all border border-white/30 backdrop-blur-md text-white shadow-lg active:scale-95 group flex items-center justify-center"
              >
                <FaRedo className={`w-3 h-3 sm:w-4 sm:h-4 ${isRefreshing ? "animate-spin" : "group-hover:rotate-180 transition-transform duration-500"}`} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <AdsSubNav type="free" />

      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6">
        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <div className="flex flex-col items-center gap-4">
              <FaSpinner className="animate-spin text-orange-500 text-4xl" />
              <p className="text-slate-400 font-black uppercase text-[10px] tracking-widest">Loading Live Ads...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-3 shadow-sm">
            <FaTimes className="flex-shrink-0" />
            <p className="text-sm font-bold">{error}</p>
          </div>
        )}

        {/* No Running Free Ads */}
        {!loading && !error && runningFreeAds.length === 0 && (
          <div className="text-center py-20 bg-white rounded-3xl border-2 border-dashed border-slate-100 shadow-sm px-6">
            <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaAd className="text-3xl text-slate-200" />
            </div>
            <h3 className="text-xl font-black text-slate-900 tracking-tight">No reward ads are currently running</h3>
            <p className="text-slate-400 text-sm font-medium mt-2 max-w-xs mx-auto italic">Accepted tasks that you have submitted will appear here while they are being processed.</p>
          </div>
        )}

        {/* Running Free Ads List */}
        {!loading && !error && runningFreeAds.length > 0 && (
          <>
            {/* Mobile View - Compact Cards */}
            <div className="sm:hidden space-y-4">
              {paginatedAds.map((ad, index) => (
                <div key={index} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex h-32 transform transition-all active:scale-[0.98] ring-1 ring-slate-50">
                  {/* Left: Media Preview */}
                  <div className="w-28 bg-slate-50 flex-shrink-0 relative overflow-hidden group">
                    {ad.screenshot ? (
                      <img
                        src={getMediaUrl(ad.screenshot)}
                        alt="Ad"
                        className="w-full h-full object-cover"
                        onClick={() => window.open(getMediaUrl(ad.screenshot), '_blank')}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-300">
                        <FaAd className="text-2xl opacity-10" />
                      </div>
                    )}

                    {/* Badge Overlay */}
                    <div className="absolute top-2 left-2 flex flex-col gap-1">
                      <span className="px-2 py-0.5 rounded-md text-[8px] font-black uppercase bg-amber-500 text-white shadow-lg ring-1 ring-white/20">
                        RUNNING
                      </span>
                    </div>
                  </div>

                  {/* Right: Details Section */}
                  <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
                    <div className="space-y-1.5">
                      <div className="flex justify-between items-start gap-1">
                        <h3 className="text-slate-900 font-black text-[10px] uppercase tracking-tight truncate leading-tight flex-1">
                          {ad.channelName || "Channel Ad"}
                        </h3>
                        <span className="text-indigo-600 font-black text-[9px] whitespace-nowrap bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-100 uppercase tracking-tighter">
                          {ad.platform || "---"}
                        </span>
                      </div>

                      <div className="space-y-1 mt-1">
                        <p className="text-slate-400 text-[8px] font-bold uppercase tracking-tight truncate font-mono bg-slate-50 px-1.5 py-0.5 rounded w-fit">
                          ID: {(ad.adId || "").slice(-8).toUpperCase()}
                        </p>
                        <div className="flex items-center gap-1.5 text-slate-500">
                          <FaCalendarAlt className="text-[9px] text-slate-300" />
                          <span className="text-[9px] font-bold text-slate-400 italic">
                            {new Date(ad.submittedAt).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 mt-auto">
                      {ad.videoLink && (
                        <button
                          onClick={() => window.open(ad.videoLink, '_blank')}
                          className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-[8px] font-black uppercase tracking-widest rounded-lg flex items-center justify-center gap-1.5 transition-all active:scale-95 shadow-md shadow-indigo-100"
                        >
                          <FaEye className="text-[10px]" />
                          Link
                        </button>
                      )}
                      <div className="flex-1 py-2 bg-slate-100 text-slate-500 text-[8px] font-black uppercase tracking-widest rounded-lg flex items-center justify-center gap-1.5">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" />
                        Live
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop View - Table */}
            <div className="hidden sm:block overflow-x-auto bg-white rounded-2xl border border-slate-100 shadow-xl overflow-hidden mt-6">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Preview</th>
                    <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Ad Info</th>
                    <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Platform</th>
                    <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Video Link</th>
                    <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Submitted At</th>
                    <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {paginatedAds.map((ad, index) => (
                    <tr key={index} className="hover:bg-slate-50/50 transition-colors group">
                      <td className="px-6 py-4">
                        {ad.screenshot ? (
                          <div className="relative w-16 h-16 rounded-xl overflow-hidden border border-slate-200">
                            <img
                              src={getMediaUrl(ad.screenshot)}
                              alt="Ad"
                              className="w-full h-full object-cover transition-transform group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                              onClick={() => window.open(getMediaUrl(ad.screenshot), '_blank')}>
                              <FaEye className="text-white" />
                            </div>
                          </div>
                        ) : (
                          <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center">
                            <FaAd className="text-slate-300" />
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-black text-slate-900">{ad.channelName || "No Name"}</span>
                          <span className="text-[10px] font-mono font-bold text-slate-400 mt-0.5 uppercase tracking-tighter">ID: {ad.adId || "---"}</span>
                          <span className="text-[9px] font-bold text-indigo-500 mt-1 uppercase">Iinsaf: {ad.iinsafId || "---"}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-3 py-1 bg-purple-50 text-purple-700 text-[10px] font-black rounded-full border border-purple-100 uppercase tracking-widest">
                          {ad.platform || "---"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {ad.videoLink ? (
                          <a href={ad.videoLink} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-bold text-xs bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100 transition-colors">
                            <FaEye /> View URL
                          </a>
                        ) : "---"}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-xs font-bold text-slate-600">
                            {new Date(ad.submittedAt).toLocaleDateString("en-GB", { day: '2-digit', month: 'short', year: 'numeric' })}
                          </span>
                          <span className="text-[10px] text-slate-400 font-medium">
                            {new Date(ad.submittedAt).toLocaleTimeString("en-GB", { hour: '2-digit', minute: '2-digit', hour12: true })}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 text-amber-700 text-[10px] font-black rounded-full border border-amber-100 uppercase tracking-widest shadow-sm">
                          <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                          {ad.status || "Live"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-8 pb-10">
                <nav className="inline-flex items-center gap-2 bg-white p-2 rounded-2xl shadow-lg border border-slate-100">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2.5 rounded-xl border border-slate-100 text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                  >
                    <FaArrowLeft size={12} />
                  </button>
                  <div className="flex gap-1.5 px-2">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-10 h-10 rounded-xl text-xs font-black transition-all ${currentPage === pageNum
                          ? 'bg-indigo-600 text-white shadow-md shadow-indigo-100 scale-110'
                          : 'text-slate-500 hover:bg-slate-50'
                          }`}
                      >
                        {pageNum}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2.5 rounded-xl border border-slate-100 text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all rotate-180"
                  >
                    <FaArrowLeft size={12} />
                  </button>
                </nav>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RunningFreeAds;
